import React from "react";
import ui from "../../pages/dashboard/DashboardUI.module.css";

// Helper to get initials from a name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Helper to generate a consistent color based on name
const getAvatarColor = (name) => {
  const colors = [
    '#7c3aed', '#2563eb', '#059669', '#d97706', '#dc2626', 
    '#db2777', '#7c3aed', '#0891b2', '#4f46e5', '#ea580c'
  ];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Render avatar stack component
const AvatarStack = ({ assignees = [], totalCount = 0, maxDisplay = 3 }) => {
  if (!assignees || assignees.length === 0) return null;
  
  const displayedAssignees = assignees.slice(0, maxDisplay);
  const remaining = totalCount - maxDisplay;
  
  return (
    <div className={ui.avatarStack}>
      {displayedAssignees.map((assignee, index) => (
        <div
          key={assignee.user_id}
          className={ui.avatar}
          style={{ 
            backgroundColor: getAvatarColor(assignee.user_name),
            zIndex: maxDisplay - index
          }}
          title={assignee.user_name}
        >
          {getInitials(assignee.user_name)}
        </div>
      ))}
      {remaining > 0 && (
        <div className={`${ui.avatar} ${ui.avatarMore}`}>
          +{remaining}
        </div>
      )}
    </div>
  );
};

export default function DataTable({
  expandedRows = {},    
  toggleRowExpand = () => {},
  columns = [],
  data = [],
  onRowClick = () => {},
  groupedBy = null,
  collapsedGroups = {},
  toggleGroupCollapse = () => {},
  customCellRender = {},
  groupOrder = [],
  onMilestoneClick = () => {},
  sortColumn = null,
  sortDirection = "asc",
  onSort = null,
  onAddColumn = null,
}) {
  const visibleColumns = columns.filter((col) => col.visible);

  // The trailing "+" header is a real column as far as the table is concerned,
  // so rows and group headers have to account for it or the grid goes ragged.
  const totalColumnCount = visibleColumns.length + (onAddColumn ? 1 : 0);

  const getUrgencyClass = (urgency) => {
    switch (urgency?.toLowerCase()) {
      case 'high': return ui.urgencyHigh;
      case 'medium': return ui.urgencyMedium;
      case 'low': return ui.urgencyLow;
      default: return '';
    }
  };

  const renderRow = (row, keyPrefix = "") => {
    const rowKey = keyPrefix + (row.project_id || row.lead_id || row.id);
    const isExpanded = expandedRows[row.project_id];
    const hasMilestones = row.milestones && row.milestones.length > 0;

    return (
      <React.Fragment key={rowKey}>
        <tr
          onClick={() => onRowClick(row)}
          className={ui.projectRow}
        >
          {visibleColumns.map((col, colIndex) => {
            if (col.key === "client_company") {
              return (
                <td key={col.key} className={ui.projectNameCell}>
                  <div className={ui.projectNameWrapper}>
                    {hasMilestones && (
                      <button
                        type="button"
                        className={ui.expandBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowExpand(row.project_id);
                        }}
                      >
                        <span className={`${ui.expandIcon} ${isExpanded ? ui.expanded : ''}`}>
                          ›
                        </span>
                      </button>
                    )}
                    {!hasMilestones && <span className={ui.expandPlaceholder} />}
                    {(row.display_color || row.status_color || row.action_color) && (
                      <span 
                        className={ui.projectColorDot} 
                        style={{ backgroundColor: row.display_color || row.status_color || row.action_color }}
                        title={row.display_color ? "Lead color indicator" : (row.status_color ? "Project status color" : "Lead action color")}
                      />
                    )}
                    <span className={ui.projectNameText}>{row[col.key] || "—"}</span>
                    {hasMilestones && (
                      <span className={ui.milestoneBadge}>
                        {row.milestones.length}
                      </span>
                    )}
                  </div>
                </td>
              );
            }

            if (col.key === "assignees") {
              return (
                <td key={col.key} className={ui.assigneesCell}>
                  <AvatarStack 
                    assignees={row.assignees} 
                    totalCount={row.assignee_count || 0} 
                    maxDisplay={4} 
                  />
                </td>
              );
            }

            if (col.key === "urgency" && !customCellRender[col.key]) {
              return (
                <td key={col.key}>
                  <span className={`${ui.urgencyPill} ${getUrgencyClass(row.urgency)}`}>
                    {row.urgency || "—"}
                  </span>
                </td>
              );
            }

            return (
              <td key={col.key} className={col.key === 'status' ? ui.statusCell : ''}>
                {customCellRender[col.key]
                  ? customCellRender[col.key](row)
                  : row[col.key] || "—"}
              </td>
            );
          })}
          {onAddColumn && <td className={ui.addColumnCell} />}
        </tr>

        {/* Expanded milestone rows */}
        {isExpanded && hasMilestones && (
          <tr key={`milestones-${row.project_id}`} className={ui.milestoneExpandedRow}>
            <td colSpan={totalColumnCount} className={ui.milestoneExpandedCell}>
              <div className={ui.milestoneContainerCompact}>
                {row.milestones.map((m) => {
                  // Calculate progress percentage based on dates
                  const startDate = m.start_date ? new Date(m.start_date) : null;
                  const dueDate = m.due_date ? new Date(m.due_date) : null;
                  const today = new Date();
                  
                  let progress = 0;
                  if (m.status?.toLowerCase() === 'complete' || m.status?.toLowerCase() === 'completed') {
                    progress = 100;
                  } else if (startDate && dueDate) {
                    const totalDuration = Math.max(1, dueDate - startDate);
                    const elapsed = today - startDate;
                    progress = totalDuration > 1 ? Math.min(100, Math.max(0, (elapsed / totalDuration) * 100)) : 100;
                  }
                  
                  const isComplete = m.status?.toLowerCase() === 'complete' || m.status?.toLowerCase() === 'completed';
                  const isOverdue = dueDate && today >= dueDate && !isComplete;
                  
                  return (
                    <div 
                      key={m.milestone_id || m.temp_id} 
                      className={ui.milestoneCardInline}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMilestoneClick(m);
                      }}
                    >
                      <div className={ui.milestoneInlineContent}>
                        <div className={ui.milestoneInlineLeft}>
                          <span className={ui.milestoneInlineName}>{m.milestone_name}</span>
                          <span className={ui.milestoneInlineDates}>
                            {startDate ? startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                            <span className={ui.milestoneInlineArrow}>→</span>
                            {dueDate ? dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                          </span>
                        </div>
                        <div className={ui.milestoneInlineRight}>
                          <AvatarStack 
                            assignees={m.assignees} 
                            totalCount={m.assignee_count || 0} 
                            maxDisplay={2} 
                          />
                          <span className={`${ui.milestoneInlineStatus} ${ui[`milestone_${m.status?.replace(/\s+/g, '_')}`] || ''}`}>
                            {m.status || 'open'}
                          </span>
                        </div>
                        <div className={ui.milestoneInlineCenter}>
                          <div className={ui.milestoneInlineProgress}>
                            <div className={ui.milestoneInlineProgressTrack}>
                              <div 
                                className={`${ui.milestoneInlineProgressFill} ${isComplete ? ui.progressComplete : ''} ${isOverdue ? ui.progressOverdue : ''}`}
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                            <span className={ui.milestoneInlinePercent}>{Math.round(progress)}%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };


  const renderGroupedRows = () => {
    const grouped = data.reduce((acc, row) => {
    const groupKey =
      typeof groupedBy === "function" ? groupedBy(row) : row[groupedBy] || "Other";

    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(row);
    return acc;
  }, {});


    let entries = Object.entries(grouped);

    if (groupOrder.length > 0) {
      entries = entries.sort(
        ([a], [b]) => groupOrder.indexOf(a) - groupOrder.indexOf(b)
      );
    }

    return entries.map(([group, rows]) => {
      const isCollapsed = collapsedGroups[group] || false;

      return (
        <React.Fragment key={group}>
          <tr
            className={ui.groupHeaderRow}
            onClick={() => toggleGroupCollapse(group)}
          >
            <td colSpan={totalColumnCount}>
              <div className={ui.groupHeader}>
                <span className={`${ui.groupExpandIcon} ${isCollapsed ? '' : ui.expanded}`}>
                  ›
                </span>
                <span className={ui.groupName}>{group}</span>
                <span className={ui.groupCount}>{rows.length}</span>
              </div>
            </td>
          </tr>
          {!isCollapsed && rows.map((row) => renderRow(row, group + "-"))}
        </React.Fragment>
      );
    });
  };

  return (
    <div className={ui.tableWrapper}>
      <table className={ui.projectTable}>
        <thead>
          <tr>
            {visibleColumns.map((col) => (
              <th
                key={col.key}
                onClick={onSort ? () => onSort(col.key) : undefined}
                className={onSort ? ui.sortableHeader : ''}
                style={onSort ? { cursor: 'pointer' } : undefined}
              >
                <span className={ui.headerContent}>
                  {col.label}
                  {onSort && sortColumn === col.key && (
                    <span className={ui.sortIndicator}>
                      {sortDirection === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </span>
              </th>
            ))}
            {onAddColumn && (
              <th className={ui.addColumnHeader}>
                <button
                  type="button"
                  className={ui.addColumnButton}
                  onClick={onAddColumn}
                  title="Add a column"
                  aria-label="Add a column"
                >
                  +
                </button>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {groupedBy ? renderGroupedRows() : data.map((row) => renderRow(row))}
        </tbody>
      </table>
    </div>
  );
}